import { space, useNarrowViewport } from '@viq-admin/ui';
import { MetricCard, type MetricTone } from './MetricCard';
import { ScansGoalCard } from './ScansGoalCard';

interface Props {
  totalScans?: number;
  /** The event's scans goal (`kpi.scans_goal`). Only a positive number turns
   *  the Scans tile into Scans │ Goal │ %; null, undefined (no backend sends
   *  it yet), 0 or anything else keeps the plain Scans tile. */
  scansGoal?: number | null;
  totalNotes?: number;
  totalTasks?: number;
  meetingsBooked?: number;
  notesTone?: MetricTone;
  notesCaption?: string;
}

function hasScansGoal(goal: number | null | undefined): goal is number {
  return typeof goal === 'number' && Number.isFinite(goal) && goal > 0;
}

export function KpiTiles({
  totalScans,
  scansGoal,
  totalNotes,
  totalTasks,
  meetingsBooked,
  notesTone,
  notesCaption,
}: Props) {
  const narrow = useNarrowViewport();
  return (
    <div
      data-testid="batch-kpi-tiles"
      style={{
        display: 'grid',
        // minmax(0, 1fr), not 1fr: columns stay equal whatever their content,
        // so this row lines up with the Volume & Coverage strip below (which
        // uses the same template). Two by two on a phone, where a quarter of
        // the width can't hold a tile's number.
        gridTemplateColumns: `repeat(${narrow ? 2 : 4}, minmax(0, 1fr))`,
        gap: space.md,
        padding: '16px 16px 8px',
      }}
    >
      {hasScansGoal(scansGoal) ? (
        narrow ? (
          // Scans │ Goal │ % is three numbers: on a phone it takes the whole row.
          <div style={{ gridColumn: '1 / -1' }}>
            <ScansGoalCard scans={totalScans} goal={scansGoal} />
          </div>
        ) : (
          <ScansGoalCard scans={totalScans} goal={scansGoal} />
        )
      ) : (
        <MetricCard label="Scans" value={totalScans} />
      )}
      <MetricCard label="Notes" value={totalNotes} tone={notesTone} caption={notesCaption} />
      <MetricCard label="Tasks" value={totalTasks} />
      <MetricCard label="Meetings Booked" value={meetingsBooked} />
    </div>
  );
}
